/** Columnas del CSV de stock.
 *
 * El IMPORT refleja el formulario "Agregar stock" (ingreso por `receiveStock`):
 * `cantidad` es la cantidad a INGRESAR (se suma al stock actual) y se capturan
 * costo, motivo, proveedor, vencimiento y lote igual que en el formulario.
 *
 * El EXPORT es un snapshot del estado actual de la sucursal (no un ingreso),
 * por eso incluye `costo_promedio` y no las columnas de movimiento (motivo/lote).
 */
export const STOCK_CSV_IMPORT_HEADERS = [
  'sku',
  'cantidad',
  'costo_unitario',
  'motivo',
  'proveedor',
  'stock_minimo',
  'vencimiento',
  'lote',
] as const;

/** Solo estas columnas son obligatorias para importar. */
export const STOCK_CSV_REQUIRED_IMPORT_HEADERS = ['sku', 'cantidad'] as const;

export const STOCK_CSV_EXPORT_HEADERS = [
  'sku',
  'nombre',
  'unidad',
  'cantidad',
  'stock_minimo',
  'costo_promedio',
  'proveedor',
  'vencimiento',
] as const;

/** True si el archivo es el export de “stock actual” (no un ingreso). */
export function looksLikeStockSnapshotExport(headers: readonly string[]): boolean {
  const set = new Set(headers.map((h) => h.trim().toLowerCase()));
  if (set.has('costo_promedio')) return true;
  return set.has('unidad') && set.has('nombre') && !set.has('motivo') && !set.has('lote');
}

export type StockCsvMotivo = 'purchase_reception' | 'stock_restock' | 'stock_initial_load';

/**
 * Interpreta la columna `motivo` (acepta etiquetas en español o las claves internas).
 * - `undefined`: celda vacía (el llamador aplica el default según haya o no stock previo).
 * - `'invalid'`: valor no reconocido (error de validación).
 */
export function parseStockCsvMotivo(raw: string | undefined): StockCsvMotivo | 'invalid' | undefined {
  if (raw == null || raw.trim() === '') return undefined;
  const v = raw.trim().toLowerCase();
  if (['recepcion', 'recepción', 'recepcion_compra', 'compra', 'purchase_reception', 'purchase'].includes(v)) {
    return 'purchase_reception';
  }
  if (['reposicion', 'reposición', 'restock', 'stock_restock'].includes(v)) {
    return 'stock_restock';
  }
  if (['alta_inicial', 'alta inicial', 'carga_inicial', 'carga inicial', 'inicial', 'stock_initial_load', 'initial'].includes(v)) {
    return 'stock_initial_load';
  }
  return 'invalid';
}

export const STOCK_CSV_MOTIVO_LABELS: Record<StockCsvMotivo, string> = {
  purchase_reception: 'recepcion',
  stock_restock: 'reposicion',
  stock_initial_load: 'alta_inicial',
};

/**
 * Valida un vencimiento ISO `YYYY-MM-DD`.
 * - `undefined`: celda vacía (sin vencimiento).
 * - `'invalid'`: formato no válido.
 */
export function parseStockCsvExpiry(raw: string | undefined): string | 'invalid' | undefined {
  if (raw == null || raw.trim() === '') return undefined;
  const v = raw.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return 'invalid';
  const parsed = new Date(`${v}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return 'invalid';
  return v;
}

/**
 * Filas de ejemplo de la plantilla de ingreso: una por cada `motivo` posible
 * (orden de columnas según STOCK_CSV_IMPORT_HEADERS). `proveedor` se muestra como
 * nombre para dejar claro que no hace falta el id.
 */
export function stockCsvTemplateExampleRows(): string[][] {
  return [
    // recepcion: compra a proveedor, con costo, vencimiento y lote.
    ['EJ-SKU-001', '24', '120,50', 'recepcion', 'EJEMPLO Proveedor SA', '6', '2026-12-31', 'L-2026-001'],
    // reposicion: reposición sin proveedor ni vencimiento.
    ['EJ-SKU-002', '12', '118,00', 'reposicion', '', '3', '', ''],
    // alta_inicial: carga inicial de stock (primera vez del producto).
    ['EJ-SKU-003', '50', '100,00', 'alta_inicial', '', '5', '', ''],
  ];
}

export function stockCsvTemplateFilename(): string {
  return 'plantilla_stock.csv';
}

export function stockCsvExportFilename(branchLabel?: string): string {
  const stamp = new Date().toISOString().slice(0, 10);
  const safe = (branchLabel ?? 'sucursal').replace(/[^\w\-]+/g, '_').slice(0, 24);
  return `stock_${safe}_${stamp}.csv`;
}
